/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ExternalMCPPackage,
  ExternalMCPPackage$inboundSchema,
} from "./externalmcppackage.js";
import {
  ExternalMCPRemote,
  ExternalMCPRemote$inboundSchema,
} from "./externalmcpremote.js";
import {
  ExternalMCPRepository,
  ExternalMCPRepository$inboundSchema,
} from "./externalmcprepository.js";

/**
 * Advertised authentication setup; unknown means evidence is insufficient, not that manual setup is required.
 */
export const AuthSetup = {
  None: "none",
  Dcr: "dcr",
  Cimd: "cimd",
  Manual: "manual",
  Unknown: "unknown",
} as const;
/**
 * Advertised authentication setup; unknown means evidence is insufficient, not that manual setup is required.
 */
export type AuthSetup = OpenEnum<typeof AuthSetup>;

/**
 * A summary of an MCP server from an external registry, returned by catalog listings
 */
export type ExternalMCPServerEntry = {
  /**
   * Advertised authentication setup; unknown means evidence is insufficient, not that manual setup is required.
   */
  authSetup?: AuthSetup | undefined;
  /**
   * Description of what the server does
   */
  description: string;
  /**
   * URL to the server's icon
   */
  iconUrl?: string | undefined;
  /**
   * Whether every tool on the server is read-only
   */
  isReadOnly: boolean;
  /**
   * ID of the attached MCP server backing this server
   */
  mcpServerId?: string | undefined;
  /**
   * Opaque metadata from the registry
   */
  meta?: any | undefined;
  /**
   * Published packages that run this server, when the registry declares any
   */
  packages?: Array<ExternalMCPPackage> | undefined;
  /**
   * ID of the external MCP registry this server came from
   */
  registryId?: string | undefined;
  /**
   * Server specifier used to look up in the registry (e.g., 'io.github.user/server')
   */
  registrySpecifier: string;
  /**
   * Available remote endpoints for the server
   */
  remotes?: Array<ExternalMCPRemote> | undefined;
  /**
   * The source repository a registry entry links for its server. A registry declaration: nothing verifies the endpoint runs this code.
   */
  repository?: ExternalMCPRepository | undefined;
  /**
   * Whether issuer metadata advertises compatible Client ID Metadata Document support.
   */
  supportsCimd?: boolean | undefined;
  /**
   * Whether the server's OAuth authorization server advertises a dynamic client registration endpoint (RFC 7591). False alone does not establish that manual setup is required.
   */
  supportsDcr: boolean;
  /**
   * Display name for the server
   */
  title?: string | undefined;
  /**
   * Number of tools the server exposes
   */
  toolCount: number;
  /**
   * ID of the attached toolset backing this server
   */
  toolsetId?: string | undefined;
  /**
   * Semantic version of the server
   */
  version: string;
};

/** @internal */
export const AuthSetup$inboundSchema: z.ZodMiniType<AuthSetup, unknown> =
  openEnums.inboundSchema(AuthSetup);

/** @internal */
export const ExternalMCPServerEntry$inboundSchema: z.ZodMiniType<
  ExternalMCPServerEntry,
  unknown
> = z.pipe(
  z.object({
    auth_setup: z.optional(AuthSetup$inboundSchema),
    description: z.string(),
    icon_url: z.optional(z.string()),
    is_read_only: z.boolean(),
    mcp_server_id: z.optional(z.string()),
    meta: z.optional(z.any()),
    packages: z.optional(z.array(ExternalMCPPackage$inboundSchema)),
    registry_id: z.optional(z.string()),
    registry_specifier: z.string(),
    remotes: z.optional(z.array(ExternalMCPRemote$inboundSchema)),
    repository: z.optional(ExternalMCPRepository$inboundSchema),
    supports_cimd: z.optional(z.boolean()),
    supports_dcr: z.boolean(),
    title: z.optional(z.string()),
    tool_count: z.int(),
    toolset_id: z.optional(z.string()),
    version: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "auth_setup": "authSetup",
      "icon_url": "iconUrl",
      "is_read_only": "isReadOnly",
      "mcp_server_id": "mcpServerId",
      "registry_id": "registryId",
      "registry_specifier": "registrySpecifier",
      "supports_cimd": "supportsCimd",
      "supports_dcr": "supportsDcr",
      "tool_count": "toolCount",
      "toolset_id": "toolsetId",
    });
  }),
);

export function externalMCPServerEntryFromJSON(
  jsonString: string,
): SafeParseResult<ExternalMCPServerEntry, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ExternalMCPServerEntry$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ExternalMCPServerEntry' from JSON`,
  );
}
